// src/components/EstateDecisionsPanel.jsx
// Journal des décisions de l'estate : qui a validé quelle mise en vente, quel prix, quelle authentification, quel prêt.
// Mémoire des décisions, saisie par le propriétaire : ARTICHAUT n'authentifie rien, ne qualifie aucun droit.
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";

const TYPES = ["release", "price", "authentication", "loan", "sale", "licence", "other"];
const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = () => ({ decided_on: today(), decision_type: "release", subject: "", decided_by: "", note: "", artwork_id: "" });

const TEXT = {
  fr: {
    title: "Journal des décisions", add: "Noter une décision", save: "Enregistrer la décision", cancel: "Annuler", del: "Supprimer", confirmDel: "Supprimer cette décision du journal ?",
    empty: "Aucune décision notée.", err: "Enregistrement impossible : ", errSubject: "Indiquez ce qui a été décidé.", allEstate: "Tout l'estate",
    f: { decided_on: "Date", decision_type: "Type", subject: "Ce qui a été décidé", decided_by: "Qui a validé (nom, qualité)", note: "Note (document de base, conditions…)", artwork_id: "Œuvre concernée" },
    types: { release: "Mise en vente", price: "Prix", authentication: "Authentification", loan: "Prêt", sale: "Vente", licence: "Licence", other: "Autre" },
  },
  en: {
    title: "Decision log", add: "Record a decision", save: "Save the decision", cancel: "Cancel", del: "Delete", confirmDel: "Delete this decision from the log?",
    empty: "No decision recorded.", err: "Could not save: ", errSubject: "Enter what was decided.", allEstate: "The whole estate",
    f: { decided_on: "Date", decision_type: "Type", subject: "What was decided", decided_by: "Who approved it (name, capacity)", note: "Note (supporting document, conditions…)", artwork_id: "Work concerned" },
    types: { release: "Release for sale", price: "Price", authentication: "Authentication", loan: "Loan", sale: "Sale", licence: "Licence", other: "Other" },
  },
  de: {
    title: "Entscheidungsprotokoll", add: "Entscheidung festhalten", save: "Entscheidung speichern", cancel: "Abbrechen", del: "Löschen", confirmDel: "Diese Entscheidung aus dem Protokoll löschen?",
    empty: "Keine Entscheidung festgehalten.", err: "Speichern nicht möglich: ", errSubject: "Geben Sie an, was entschieden wurde.", allEstate: "Der gesamte Nachlass",
    f: { decided_on: "Datum", decision_type: "Art", subject: "Was entschieden wurde", decided_by: "Wer zugestimmt hat (Name, Funktion)", note: "Notiz (Beleg, Bedingungen …)", artwork_id: "Betroffenes Werk" },
    types: { release: "Freigabe zum Verkauf", price: "Preis", authentication: "Authentifizierung", loan: "Leihgabe", sale: "Verkauf", licence: "Lizenz", other: "Andere" },
  },
};

const box = { width: "100%", boxSizing: "border-box", padding: "7px 9px", border: "1px solid #e5e3df", borderRadius: 4, fontSize: 13, fontFamily: "system-ui, sans-serif", background: "#fff" };
const btn = (primary) => ({ padding: "6px 12px", borderRadius: 2, fontSize: 12.5, cursor: "pointer", border: "1px solid #1a1a1a", background: primary ? "#1a1a1a" : "#fff", color: primary ? "#fff" : "#1a1a1a", fontWeight: 600 });

export default function EstateDecisionsPanel({ collectionId }) {
  const { i18n } = useTranslation();
  const L = TEXT[String(i18n?.resolvedLanguage || i18n?.language || "fr").slice(0, 2)] || TEXT.fr;
  const [rows, setRows] = useState([]);
  const [works, setWorks] = useState([]);
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    const { data } = await supabase.from("estate_decisions").select("*").eq("collection_id", collectionId)
      .order("decided_on", { ascending: false }).order("created_at", { ascending: false });
    setRows(data || []);
  }, [collectionId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    supabase.from("artworks_secure").select("id,title,inventory_number").eq("collection_id", collectionId).order("inventory_number").limit(1000)
      .then(({ data }) => setWorks(data || []));
  }, [collectionId]);

  const workName = (id) => { const w = works.find((x) => x.id === id); return w ? [w.inventory_number, w.title].filter(Boolean).join(" · ") : null; };

  const save = async () => {
    setMsg("");
    if (!String(form.subject || "").trim()) { setMsg(L.err + L.errSubject); return; }
    const row = {
      collection_id: collectionId, decided_on: form.decided_on || today(), decision_type: TYPES.includes(form.decision_type) ? form.decision_type : "other",
      subject: form.subject.trim(), decided_by: String(form.decided_by || "").trim() || null, note: String(form.note || "").trim() || null,
      artwork_id: form.artwork_id || null,
    };
    const { error } = await supabase.from("estate_decisions").insert(row);
    if (error) { setMsg(L.err + error.message); return; }
    setForm(null); load();
  };
  const remove = async (id) => {
    if (!window.confirm(L.confirmDel)) return;
    await supabase.from("estate_decisions").delete().eq("id", id);
    load();
  };

  return (
    <div style={{ margin: "14px 0 0" }}>
      <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#8a8378", fontWeight: 700, marginBottom: 8 }}>{L.title}</div>
      {rows.length === 0 && !form && <div style={{ fontSize: 12.5, color: "#8a8378" }}>{L.empty}</div>}
      <div style={{ display: "grid", gap: 8 }}>
        {rows.map((d) => (
          <div key={d.id} style={{ borderLeft: "3px solid #c9a961", background: "#faf9f7", padding: "8px 10px", fontSize: 13 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
              <span><strong>{d.decided_on}</strong> · {L.types[d.decision_type] || d.decision_type}</span>
              <button type="button" style={{ ...btn(false), color: "#8f3a44", borderColor: "#8f3a44", padding: "2px 8px" }} onClick={() => remove(d.id)}>{L.del}</button>
            </div>
            <div>{d.subject}</div>
            <div style={{ color: "#6f695f", fontSize: 12.5 }}>
              {[d.artwork_id ? workName(d.artwork_id) : L.allEstate, d.decided_by].filter(Boolean).join(" · ")}
            </div>
            {d.note && <div style={{ color: "#8a8378", fontSize: 12 }}>{d.note}</div>}
          </div>
        ))}
      </div>
      {form && (
        <div style={{ border: "1px solid #c9a961", borderRadius: 6, padding: 10, marginTop: 8, display: "grid", gap: 8 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <label style={{ display: "grid", gap: 3, fontSize: 12.5, color: "#4a453d" }}>
              <span style={{ fontWeight: 600 }}>{L.f.decided_on}</span>
              <input style={{ ...box, width: 170 }} type="date" value={form.decided_on} onChange={(e) => setForm({ ...form, decided_on: e.target.value })} />
            </label>
            <label style={{ display: "grid", gap: 3, fontSize: 12.5, color: "#4a453d" }}>
              <span style={{ fontWeight: 600 }}>{L.f.decision_type}</span>
              <select style={{ ...box, width: 200 }} value={form.decision_type} onChange={(e) => setForm({ ...form, decision_type: e.target.value })}>
                {TYPES.map((tp) => <option key={tp} value={tp}>{L.types[tp]}</option>)}
              </select>
            </label>
            <label style={{ display: "grid", gap: 3, fontSize: 12.5, color: "#4a453d", flex: 1, minWidth: 200 }}>
              <span style={{ fontWeight: 600 }}>{L.f.artwork_id}</span>
              <select style={box} value={form.artwork_id} onChange={(e) => setForm({ ...form, artwork_id: e.target.value })}>
                <option value="">{L.allEstate}</option>
                {works.map((w) => <option key={w.id} value={w.id}>{[w.inventory_number, w.title].filter(Boolean).join(" · ")}</option>)}
              </select>
            </label>
          </div>
          {["subject", "decided_by", "note"].map((k) => (
            <label key={k} style={{ display: "grid", gap: 3, fontSize: 12.5, color: "#4a453d" }}>
              <span style={{ fontWeight: 600 }}>{L.f[k]}</span>
              {k === "note" ? <textarea style={{ ...box, minHeight: 52 }} value={form[k] || ""} maxLength={2000} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                : <input style={box} value={form[k] || ""} maxLength={k === "subject" ? 500 : 300} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />}
            </label>
          ))}
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button type="button" style={btn(true)} onClick={save}>{L.save}</button>
            <button type="button" style={btn(false)} onClick={() => { setForm(null); setMsg(""); }}>{L.cancel}</button>
            {msg && <span style={{ fontSize: 12.5, color: "#8f3a44" }}>{msg}</span>}
          </div>
        </div>
      )}
      {!form && <div style={{ marginTop: 8 }}><button type="button" style={btn(false)} onClick={() => { setMsg(""); setForm(EMPTY()); }}>{L.add}</button></div>}
    </div>
  );
}
